import CustomPagination from '@/platform/common/components/CustomPagination/CustomPagination';
import NoDataRow from '@/platform/common/components/NoDataRow/NoDataRow';
import SearchBarFilter from '@/platform/common/components/SearchBarFilter/SearchBarFilter';
import { useFilters } from '@/platform/hooks/useFilters';
import { localDateToMinutes } from '@/platform/lib/utils';
import { useConfirmationStore } from '@/platform/stores/useConfirmationStore';
import {
    Button,
    Container,
    Menu,
    MenuItem,
    MenuItems,
    MenuSeparator,
    Segment,
    SegmentContent,
    SegmentFooter,
    SegmentHeader,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
} from '@/platform/ui';
import { MenuButton } from '@headlessui/react';
import {
    EllipsisVerticalIcon,
    EnvelopeIcon,
    LockClosedIcon,
    PencilIcon,
    PlusIcon,
    TrashIcon,
} from '@heroicons/react/24/outline';
import { Link, router, usePage } from '@inertiajs/react';
import { Fragment } from 'react';

const IndexView = () => {
    const { auth, users } = usePage().props;
    const { updateFilters, isSorted } = useFilters(['users']);
    const confirm = useConfirmationStore(state => state.confirm);

    const sendPassword = user => {
        router.post(
            `/users/${user.id}/send-password`,
            {},
            {
                preserveScroll: true,
                preserveState: true,
            }
        );
    };

    const handleDelete = user =>
        confirm({
            title: 'Gebruiker verwijderen?',
            description: `Weet je zeker dat je ${user.name} wilt verwijderen?`,
            confirmLabel: 'Verwijderen',
            onConfirm: close => {
                router.delete(`/users/${user.id}`, {
                    preserveScroll: true,
                    preserveState: true,
                    onFinish: close,
                });
            },
        });

    return (
        <Container>
            <Segment>
                <SegmentHeader
                    header={`Gebruikers (${users?.meta?.total || 0})`}
                >
                    {auth?.permissions?.includes('create users') && (
                        <Button
                            variant='primary'
                            as={Link}
                            href='/users/create'
                        >
                            <PlusIcon className='size-5' />
                            Nieuwe gebruiker
                        </Button>
                    )}
                </SegmentHeader>
                <SegmentContent>
                    <SearchBarFilter only={['users']} />

                    <Table striped>
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell
                                    sorted={isSorted('last_name')}
                                    onClick={() =>
                                        updateFilters('sort', 'last_name')
                                    }
                                >
                                    Naam
                                </TableHeaderCell>
                                <TableHeaderCell
                                    sorted={isSorted('email')}
                                    onClick={() =>
                                        updateFilters('sort', 'email')
                                    }
                                >
                                    E-mailadres
                                </TableHeaderCell>
                                <TableHeaderCell>Rol</TableHeaderCell>
                                <TableHeaderCell>Reseller</TableHeaderCell>
                                <TableHeaderCell>
                                    Laatste ingelogd
                                </TableHeaderCell>
                                <TableHeaderCell>SSO</TableHeaderCell>
                                <TableHeaderCell textAlign='right' />
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {users?.meta?.total === 0 && (
                                <NoDataRow numberOfColumns={7} />
                            )}

                            {users?.data?.map(user => (
                                <TableRow key={user.id}>
                                    <TableCell>
                                        {auth?.permissions?.includes(
                                            'update users'
                                        ) ? (
                                            <Link
                                                href={`/users/${user.id}/edit`}
                                            >
                                                {user.name}
                                            </Link>
                                        ) : (
                                            user.name
                                        )}
                                    </TableCell>
                                    <TableCell>{user.email}</TableCell>
                                    <TableCell>
                                        {user.roles
                                            ?.map(rol => rol.name)
                                            ?.join(', ')}
                                    </TableCell>
                                    <TableCell>{user.reseller?.name}</TableCell>
                                    <TableCell>
                                        {user.last_login_at
                                            ? localDateToMinutes(
                                                  user.last_login_at
                                              )
                                            : user.has_password
                                              ? 'Ja'
                                              : 'Geen wachtwoord ingesteld'}
                                    </TableCell>
                                    <TableCell>
                                        {user.sso?.length ? (
                                            <div className='flex gap-2'>
                                                {user.sso.map(sso => (
                                                    <Fragment key={sso}>
                                                        <img
                                                            className='size-5'
                                                            src={`/images/sso/${sso}.svg`}
                                                            alt={sso}
                                                            aria-hidden='true'
                                                        />
                                                    </Fragment>
                                                ))}
                                            </div>
                                        ) : (
                                            '—'
                                        )}
                                    </TableCell>
                                    <TableCell
                                        className='w-px'
                                        singleLine
                                        textAlign='right'
                                    >
                                        {[
                                            'update users',
                                            'impersonate users',
                                            'impersonate internal users',
                                            'impersonate all users',
                                            'delete users',
                                        ]?.some(permission =>
                                            auth?.permissions?.includes(
                                                permission
                                            )
                                        ) && (
                                            <Menu>
                                                <MenuButton>
                                                    <EllipsisVerticalIcon className='size-5' />
                                                </MenuButton>
                                                <MenuItems anchor='bottom end'>
                                                    {auth?.permissions?.includes(
                                                        'update users'
                                                    ) && (
                                                        <>
                                                            <MenuItem
                                                                as={Link}
                                                                href={`/users/${user.id}/edit`}
                                                                icon={
                                                                    PencilIcon
                                                                }
                                                            >
                                                                Wijzigen
                                                            </MenuItem>
                                                            <MenuItem
                                                                onClick={() =>
                                                                    sendPassword(
                                                                        user
                                                                    )
                                                                }
                                                                icon={
                                                                    EnvelopeIcon
                                                                }
                                                            >
                                                                Verstuur een
                                                                nieuw wachtwoord
                                                            </MenuItem>
                                                        </>
                                                    )}
                                                    {[
                                                        'impersonate users',
                                                        'impersonate internal users',
                                                        'impersonate all users',
                                                    ]?.some(p =>
                                                        auth?.permissions?.includes(
                                                            p
                                                        )
                                                    ) &&
                                                        !auth?.is_impersonated &&
                                                        user.id !==
                                                            auth?.user?.id && (
                                                            <>
                                                                <MenuSeparator />

                                                                <MenuItem
                                                                    onClick={() =>
                                                                        router.post(
                                                                            `/impersonate/login/${user.id}`
                                                                        )
                                                                    }
                                                                    icon={
                                                                        LockClosedIcon
                                                                    }
                                                                >
                                                                    Inloggen als
                                                                    deze
                                                                    gebruiker
                                                                </MenuItem>
                                                            </>
                                                        )}
                                                    {auth?.permissions?.includes(
                                                        'delete users'
                                                    ) && (
                                                        <>
                                                            <MenuSeparator />

                                                            <MenuItem
                                                                className='text-red-600'
                                                                onClick={() =>
                                                                    handleDelete(
                                                                        user
                                                                    )
                                                                }
                                                                icon={TrashIcon}
                                                            >
                                                                Verwijderen
                                                            </MenuItem>
                                                        </>
                                                    )}
                                                </MenuItems>
                                            </Menu>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </SegmentContent>
                <SegmentFooter>
                    <CustomPagination meta={users?.meta} only={['users']} />
                </SegmentFooter>
            </Segment>
        </Container>
    );
};

export default IndexView;
